/*
  Get Lucky page motion — the GSAP layer for lucky_hero and lucky_draw.

  Loaded (deferred) by both sections; the guards below make a second copy of
  the tag a no-op, and every init is keyed off a dataset flag on the element
  it animates, so a theme-editor re-render re-binds only what re-rendered.

  What lives WHERE:
    - munchief-animations.js  — the shared data-animate reveals: the hero
      heading's char-reveal, the fade-ups, the How-it-works stagger. This
      file must not touch those elements or the two tweens fight over the
      same opacity.
    - munchief-lucky-draw.js  — the two-step form and the drifting clouds.
    - this file               — everything that is specific to this page's
      art: the hero's entrance and parallax, the intro photo's scrub, the
      step badges, the form card, and the press micro-interactions.

  Same contract as the rest of the theme: every starting state is set here in
  JS, never in CSS, so a blocked or slow GSAP means "no animation", never
  invisible content. Reduced motion short-circuits before any state is set.
*/
(function () {
  'use strict';

  if (window.munchiefLuckyMotionReady) return;
  window.munchiefLuckyMotionReady = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function gsapReady() {
    return typeof window.gsap !== 'undefined';
  }

  function hasScrollTrigger() {
    return typeof window.ScrollTrigger !== 'undefined';
  }

  /* Marks an element so a re-init cannot animate it twice. Returns false when
     it has already been claimed. */
  function claim(element, key) {
    if (!element || element.dataset[key] === 'true') return false;
    element.dataset[key] = 'true';
    return true;
  }

  /* ----------------------------------------------------------------------
     Hero entrance.

     The copy is not touched: the heading runs the shared char-reveal and the
     paragraph/CTA/fine print run fade-up, both from munchief-animations.js.
     What is left is the art — the sunburst, the two cut-outs and the wave —
     which the comp draws as a single frame, so here it assembles: the
     sunburst settles out of a slight over-scale, the cut-outs walk in from
     their own sides, and the wave rises into the bottom edge.

     GSAP writes the whole transform, which is why each cut-out is two boxes:
     the tilt (and, on the right, the mirror) lives on an inner span, and
     everything below animates the outer one. Nothing here may touch that
     inner span, or the lean goes with it.
     -------------------------------------------------------------------- */
  function initHero(hero) {
    if (reduced || !gsapReady()) return;
    if (!claim(hero, 'luckyHeroMotion')) return;

    var gsap = window.gsap;
    var bg = hero.querySelector('.lucky-hero__bg');
    var left = hero.querySelector('.lucky-hero__figure--left');
    var right = hero.querySelector('.lucky-hero__figure--right');
    var wave = hero.querySelector('.lucky-hero__wave');

    var timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });

    if (bg) {
      gsap.set(bg, { scale: 1.12, transformOrigin: '50% 50%' });
      timeline.to(bg, { scale: 1, duration: 1.6, ease: 'power2.out' }, 0);
    }

    /* The pair leans in from its own corner, so the entrance is that lean
       arriving: each one starts further out along the diagonal it points
       down. Rotation is left alone — that is the inner span's job. */
    if (left) {
      gsap.set(left, { xPercent: -26, yPercent: -14, opacity: 0 });
      timeline.to(left, { xPercent: 0, yPercent: 0, opacity: 1, duration: 0.9 }, 0.15);
    }

    if (right) {
      gsap.set(right, { xPercent: 26, yPercent: -14, opacity: 0 });
      timeline.to(right, { xPercent: 0, yPercent: 0, opacity: 1, duration: 0.9 }, 0.25);
    }

    if (wave) {
      gsap.set(wave, { yPercent: 60, opacity: 0 });
      timeline.to(wave, { yPercent: 0, opacity: 1, duration: 0.7 }, 0.35);
    }

    /* Idle breathing, started once the entrance has landed. Each figure gets
       its own period so the pair never bobs in lockstep. Chained off the
       timeline rather than given a flat delay, because the entrance is the
       thing that decides where "rest" is. */
    timeline.call(function () {
      if (left) {
        gsap.to(left, {
          yPercent: -2.2,
          duration: 3.4,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true
        });
      }
      if (right) {
        gsap.to(right, {
          yPercent: -3,
          duration: 4.1,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true
        });
      }
    });

    initHeroParallax(hero, bg, left, right);
    initHeroPointer(hero, left, right);
  }

  /* Scroll parallax: the sunburst trails the page and the cut-outs lead it,
     so the hero opens up as it leaves rather than sliding away as one flat
     picture. Scrubbed, so it is reversible and costs nothing when parked.

     The idle tween above owns yPercent on the figures, so this layer moves
     them on `y` (pixels) instead — two tweens writing the same property
     would fight; two different translate components compose. */
  function initHeroParallax(hero, bg, left, right) {
    if (!hasScrollTrigger()) return;

    var gsap = window.gsap;
    var scrub = {
      trigger: hero,
      start: 'top top',
      end: 'bottom top',
      scrub: 0.6,
      invalidateOnRefresh: true
    };

    if (bg) gsap.to(bg, { y: 90, ease: 'none', scrollTrigger: scrub });
    if (left) gsap.to(left, { y: -70, ease: 'none', scrollTrigger: scrub });
    if (right) gsap.to(right, { y: -110, ease: 'none', scrollTrigger: scrub });
  }

  /* Pointer parallax, desktop only — a touch device has no hover position to
     read, and the listener would just cost scroll frames. The travel is a
     few pixels: enough that the cut-outs feel like separate cards, not
     enough to read as a drag. */
  function initHeroPointer(hero, left, right) {
    if (!finePointer || (!left && !right)) return;

    var gsap = window.gsap;
    var frame = null;
    var pointerX = 0;

    /* Only `x` is written here. The idle tween owns yPercent and the scroll
       scrub owns y, so the three layers compose into one transform instead
       of overwriting each other. */
    function apply() {
      frame = null;
      if (left) gsap.to(left, { x: pointerX * -18, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
      if (right) gsap.to(right, { x: pointerX * 18, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
    }

    hero.addEventListener('pointermove', function (event) {
      if (event.pointerType !== 'mouse') return;
      var rect = hero.getBoundingClientRect();
      if (!rect.width) return;
      // -1 .. 1 from the centre of the hero.
      pointerX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      if (frame === null) frame = window.requestAnimationFrame(apply);
    });

    hero.addEventListener('pointerleave', function () {
      pointerX = 0;
      if (left) gsap.to(left, { x: 0, duration: 1.1, ease: 'power2.out', overwrite: 'auto' });
      if (right) gsap.to(right, { x: 0, duration: 1.1, ease: 'power2.out', overwrite: 'auto' });
    });
  }

  /* ----------------------------------------------------------------------
     Intro split — the framed crew photo straightens as it scrolls through.

     The stylesheet's --lucky-intro-tilt is the rest angle (and 0deg on a
     phone, where the tilt costs width); this reads it rather than hardcoding
     -2.8deg, so a flattened photo scrubs between 0 and 0 — i.e. stays put —
     instead of tilting on exactly the widths the CSS just untilted it for.
     -------------------------------------------------------------------- */
  function initIntro(section) {
    if (reduced || !gsapReady()) return;

    var gsap = window.gsap;
    var figure = section.querySelector('.lucky-intro__figure');
    var badge = section.querySelector('.lucky-intro__badge');

    if (figure && claim(figure, 'luckyIntroMotion') && hasScrollTrigger()) {
      var rest = parseFloat(
        window.getComputedStyle(figure).getPropertyValue('--lucky-intro-tilt')
      );
      if (isNaN(rest)) rest = -2.8;

      gsap.fromTo(
        figure,
        { rotation: rest * 2.1, y: 26 },
        {
          rotation: rest,
          y: -26,
          ease: 'none',
          scrollTrigger: {
            trigger: figure,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.8,
            invalidateOnRefresh: true
          }
        }
      );
    }

    if (badge && claim(badge, 'luckyBadgeMotion')) {
      /* The badge is one word in a red chip — a fade reads as nothing, so it
         pops. Observer-driven rather than ScrollTrigger for the reason
         munchief-animations.js spells out: the photo above it lazy-loads, so
         a start position measured at DOMContentLoaded is already stale. */
      gsap.set(badge, { scale: 0.6, opacity: 0, transformOrigin: '0% 50%' });
      whenVisible(badge, function () {
        gsap.to(badge, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2.2)' });
      });
    }
  }

  /* ----------------------------------------------------------------------
     How-it-works cards.

     The cards themselves fade up through the shared fade-up-stagger hook on
     their grid — this only adds the numbered badge, which lands a beat after
     its card so the row reads as three steps counting off rather than three
     boxes appearing.
     -------------------------------------------------------------------- */
  function initSteps(section) {
    if (reduced || !gsapReady()) return;

    var gsap = window.gsap;
    var grid = section.querySelector('.lucky-steps__grid');
    if (!grid || !claim(grid, 'luckyStepsMotion')) return;

    var badges = Array.prototype.slice.call(grid.querySelectorAll('.lucky-step__number'));
    if (!badges.length) return;

    gsap.set(badges, { scale: 0, transformOrigin: '50% 50%' });

    whenVisible(grid, function () {
      gsap.to(badges, {
        scale: 1,
        duration: 0.55,
        ease: 'back.out(2.6)',
        stagger: 0.12,
        delay: 0.15 // lets the card under it arrive first
      });
    });
  }

  /* ----------------------------------------------------------------------
     Entry form.

     The card lifts in as one object; the fields inside it stagger up behind
     it. Only step 1 is on screen at that point — munchief-lucky-draw.js has
     already hidden step 2 — and a hidden fieldset's children measure as not
     intersecting, so the query is scoped to the visible step and step 2 gets
     its own transition when the visitor advances (see that file's goTo).
     -------------------------------------------------------------------- */
  function initForm(section) {
    if (reduced || !gsapReady()) return;

    var gsap = window.gsap;
    var card = section.querySelector('.lucky-form__card');
    if (!card || !claim(card, 'luckyFormMotion')) return;

    var rows = Array.prototype.slice.call(
      card.querySelectorAll('.lucky-form__step:not([hidden]) .lucky-field, .lucky-form__step:not([hidden]) .lucky-question')
    );

    gsap.set(card, { opacity: 0, y: 48, scale: 0.97, transformOrigin: '50% 100%' });
    if (rows.length) gsap.set(rows, { opacity: 0, y: 18 });

    whenVisible(card, function () {
      var timeline = gsap.timeline();
      timeline.to(card, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power3.out' });
      if (rows.length) {
        timeline.to(rows, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out', stagger: 0.07 }, '-=0.3');
      }
    });

    initFormPress(card);
  }

  /* Press feedback. The buttons already carry a CSS hover lift; this is the
     down-state, which CSS :active cannot do with a spring. Options get the
     same punch on select, so a tap registers on a touch device that has no
     hover at all. */
  function initFormPress(card) {
    var gsap = window.gsap;

    card.querySelectorAll('.lucky-form__button').forEach(function (button) {
      button.addEventListener('pointerdown', function () {
        gsap.to(button, { scale: 0.96, duration: 0.12, ease: 'power2.out', overwrite: 'auto' });
      });

      ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (type) {
        button.addEventListener(type, function () {
          gsap.to(button, { scale: 1, duration: 0.35, ease: 'back.out(3)', overwrite: 'auto' });
        });
      });
    });

    card.querySelectorAll('.lucky-option__input').forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        var option = input.closest('.lucky-option');
        if (!option) return;
        gsap.fromTo(
          option,
          { scale: 0.94 },
          { scale: 1, duration: 0.42, ease: 'back.out(3.4)', overwrite: 'auto' }
        );
      });
    });
  }

  /* ----------------------------------------------------------------------
     Cream lip — the wave that hands over to the FAQ. It rides up slightly as
     the section ends, so the join reads as the cream sliding over the pink
     rather than the two colours simply meeting.
     -------------------------------------------------------------------- */
  function initLip(section) {
    if (reduced || !gsapReady() || !hasScrollTrigger()) return;

    var lip = section.querySelector('.lucky-draw__lip-wave');
    if (!lip || !claim(lip, 'luckyLipMotion')) return;

    window.gsap.fromTo(
      lip,
      { yPercent: 18 },
      {
        yPercent: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: lip,
          start: 'top bottom',
          end: 'bottom bottom',
          scrub: 0.5,
          invalidateOnRefresh: true
        }
      }
    );
  }

  /* Shared "when this scrolls into view, once" helper. An IntersectionObserver
     rather than a ScrollTrigger, because this page lazy-loads its photos and
     a start position measured before they arrive is stale — the observer
     measures at intersection time instead. Falls back to playing immediately
     where the API is missing, which keeps the no-animation-never-invisible
     contract. */
  function whenVisible(element, play) {
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return;
    }

    new IntersectionObserver(function (entries, observer) {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      play();
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }).observe(element);
  }

  function initAll(root) {
    var scope = root || document;

    scope.querySelectorAll('.lucky-hero').forEach(initHero);
    scope.querySelectorAll('.lucky-draw').forEach(function (section) {
      initIntro(section);
      initSteps(section);
      initForm(section);
      initLip(section);
    });
  }

  function start() {
    initAll();

    /* The page's images are lazy, so every trigger start measured now is a
       guess; one refresh once they have landed settles them. */
    if (hasScrollTrigger()) window.ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* GSAP is deferred too. In document order it wins, but a section rendered
     on its own (theme editor, ?sections= re-render) can run this first — so
     everything gets one more chance once the page has fully loaded. */
  window.addEventListener('load', function () {
    initAll();
    if (hasScrollTrigger()) window.ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
